
import { MUTATIONS } from '../graphql/mutations';
import useOrderMutations from './use-order-mutations';
import { Job, JobStatus, Maybe } from '@src/graphql_types';
import { UnclaimOrderInput, JobErrorCode, MutationResponse } from '../../../graphql_types';
import {useState} from 'react';
import { NotificationCategories, useNotificationsActions } from '@shared/context/notifications';
import { useTranslation } from 'react-i18next';
import { POPUP_TYPE_ORDER } from '../components/CourierListItem';
import { trackDeliveryDahsboardOnAssignOrderComplete, trackDeliveryDashboardOnClickReAssignOrderComplete, trackDeliveryDashboardOnClickRemoveOrderComplete } from '../tracking/delivery-dashboard';
import i18n, { defaultLang } from '@src/i18n';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import useGetFeedback from '@src/shared/hooks/use-getfeedback';



interface AssignOrderPostResponse {
    assignOrder: {
        errorCode: Maybe<JobErrorCode>;
        job: Maybe<Job>;
        timestamp: string
    },
}

interface UnAssignedOrderPostResponse {
    result: MutationResponse
}


interface OrderPostVariables {
    input: UnclaimOrderInput
}

interface OrderHandlerMutationValues {
    courierId: string;
    hash: string;
    variables: {[key:string]: any}
    type: POPUP_TYPE_ORDER
}

export function useOrderMutationHandler() {
    const [isPromising,setIsPromising] = useState(false);
    const [hasNextStep, setHasNextStep] = useState(false);
    const { notifySuccess, notifyInformation, notifyError } = useNotificationsActions(NotificationCategories.Dashboard);
    const [assignOrder] = useOrderMutations<AssignOrderPostResponse,OrderPostVariables>(MUTATIONS.ASSIGN_ORDER);
    const [unAssignOrder] = useOrderMutations<UnAssignedOrderPostResponse,OrderPostVariables>(MUTATIONS.UNASSIGN_ORDER);
    const { restaurant } = useKeycloakUserState();
    const { t } = useTranslation();
    const {triggerFeedbackCampaign} = useGetFeedback();

    const handleAssignOrderMutation = async ({ courierId,hash,variables }: OrderHandlerMutationValues) => {
       setIsPromising(true);
       const {data} =  await assignOrder({variables:{input: {courierId, orderHash: hash  }}});
       if(data) {
        if(data.assignOrder.errorCode) {
            setIsPromising(false);
            return notifyError(t(`graphql.ERROR_ORDER_GENERIC`),t('notification_headers.genericErrorOrderHeader')); 
        } 
            switch(data.assignOrder.job?.status) {
                case JobStatus.ReadyForDelivery:
                    setHasNextStep(variables?.showNextStep);
                    // eslint-disable-next-line no-case-declarations
                    const commonTrackingData = {
                        language: i18n.language || defaultLang,
                        country:  restaurant?.country || '',
                        orderData: {
                          order_action: '',
                          order_id: data.assignOrder.job?.order.reference,
                          order_alcohol: data.assignOrder.job?.order.hasAlcohol,
                          order_hashed: data.assignOrder.job?.order.hash,
                          courier_vehicle: variables.vehicleType
                        }
                      }
                      
                    if(variables.assignType === POPUP_TYPE_ORDER.ASSIGN_ORDER || variables.assignType === POPUP_TYPE_ORDER.ASSIGN_ORDER_BAG) {
                        // Assign Order Success
                        if(variables.isOrderAssigned) {
                            trackDeliveryDashboardOnClickReAssignOrderComplete({
                                ...commonTrackingData,
                                orderData: {
                                    ...commonTrackingData.orderData,
                                    order_action: 'single order reassign complete'
                                }
                            })
                        } else {
                            trackDeliveryDahsboardOnAssignOrderComplete({
                                ...commonTrackingData,
                                orderData: {
                                    ...commonTrackingData.orderData,
                                    order_action: 'single order assign complete'
                                }
                            })
                        }

                    }
                    else if (variables.assignType === POPUP_TYPE_ORDER.REASSIGN_ORDER) {
                        //REAssign order Success
                        trackDeliveryDashboardOnClickReAssignOrderComplete({
                            ...commonTrackingData,
                            orderData: {
                                ...commonTrackingData.orderData,
                                order_action: 'single order reassign complete'
                            }
                        })
                    }

                    notifySuccess(t(`graphql.SUCCESS_ORDER_ASSIGN`,{orderReferance: data.assignOrder.job?.order.reference, courierName: variables.courierName}),t('notification_headers.orderAssignSuccessHeader'));
                    triggerFeedbackCampaign('mcp_users',['de','fr','nl','pl','bg','ro','dk'],restaurant?.country)
                    break;
                case JobStatus.Delivered:
                    //notifySuccess(t(`graphql.ERROR_ORDER_DELIVERED`,{orderReferance: data.assignOrder.job?.order.reference}),t('notification_headers.genericErrorOrderHeader'));
                    break;
                case JobStatus.InDelivery: 
                    //notifyInformation(t(`graphql.ERROR_ORDER_INDELIVERY`,{orderReferance: data.assignOrder.job?.order.reference,courierName: variables.courierName}),t('notification_headers.genericErrorOrderHeader'));
                    break;
                default:
                    notifyError(t(`graphql.ERROR_ORDER_GENERIC`),t('notification_headers.genericErrorOrderHeader')); 
            }
            setIsPromising(false);  

       }
       
    }

    const handleUnAssignOrderMutation = async ({ courierId,hash,variables }: OrderHandlerMutationValues) => {
        setIsPromising(true);
        const {data} = await unAssignOrder({variables:{input: {courierId, orderHash: hash  }}});
        if(data) {
            if(data.result === MutationResponse.Failure) {
                setIsPromising(false);
                return notifyError(t(`graphql.ERROR_ORDER_GENERIC`),t('notification_headers.genericErrorOrderHeader')); 
            }
            if(variables.assignType === POPUP_TYPE_ORDER.UNASSIGN_ORDER) {
                trackDeliveryDashboardOnClickRemoveOrderComplete({
                    language: i18n.language || defaultLang,
                    country:  restaurant?.country || '',
                    orderData: {
                      order_action: 'single order remove complete',
                      order_id: variables.orderId,
                      order_alcohol: variables.hasAlcohol,
                      order_hashed:hash,
                      courier_vehicle:  variables.vehicleType
                    }
                })
            }
            notifyInformation(t(`graphql.SUCCESS_ORDER_UNASSIGN`,{courierName: variables?.courierName, orderId: variables?.orderId}),t('notification_headers.orderUnassignSuccessHeader')); 
            setIsPromising(false);
            triggerFeedbackCampaign('mcp_users',['de','fr','nl','pl','bg','ro','dk'],restaurant?.country)
        }
    }

    return {
        handleAssignOrderMutation,
        handleUnAssignOrderMutation,
        isPromising,
        hasNextStep
    }
}